import React, { useEffect, useState } from 'react';
import { Button, Modal, Card, Row, Col, Badge, Alert } from 'react-bootstrap';
import {
  FiTrash2,
  FiStar,
  FiAlertTriangle,
  FiClock,
  FiCalendar,
  FiCreditCard,
  FiAlertCircle
} from 'react-icons/fi';
import { customAxiosFetch } from '../helpers/customFetch';
import { SavedCreditCards, WEFResponse } from '../Types';
import { useAppDispatch, useAppSelector } from '../hooks';
import { clearPaymentsSlice } from '../ReduxSlices/paymentsSlice';
import CardTypeIcon from '../Components/PaymentProcessing/CardTypeIcon';

interface Props {
  cardPointeProfileId: string;
  clientId: string;
}

interface ModalMetadata {
  title: string;
  bodyText: string;
  onConfirm: () => void;
}

export default function ClientSavedCreditCards({ cardPointeProfileId, clientId }: Props) {
  const [savedCreditCards, setSavedCreditCards] = useState<SavedCreditCards[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalMetaData, setModalMetaData] = useState<ModalMetadata | null>(null);
  const refreshCreditCardList = useAppSelector((app) => app.payments.refreshCreditCardList);
  const dispatch = useAppDispatch();

  useEffect(() => {
    getSavedCreditCards();
  }, []);

  useEffect(() => {
    if (refreshCreditCardList) {
      getSavedCreditCards();
      dispatch(clearPaymentsSlice());
    }
  }, [refreshCreditCardList]);

  const getSavedCreditCards = () => {
    setLoading(true);
    customAxiosFetch<WEFResponse<SavedCreditCards[]>>({
      endpoint: `/client/get-client-credit-cards/${clientId}`,
      config: { method: 'GET' },
      callback: (result) => {
        if (!result.success) return;
        setSavedCreditCards(result.data);
      },
      finalCallback: () => setLoading(false)
    });
  };

  function handleDeleteCard(cardId: string) {
    customAxiosFetch<{ success: boolean }>({
      endpoint: `/client/${clientId}/remove-credit-card/${cardId}`,
      config: { method: 'DELETE' },
      callback: (result) => {
        if (!result?.success) return;
        getSavedCreditCards();
      },
      finalCallback: () => handleCloseModal()
    });
  }

  function handleSetPrimaryCard(cardId: string) {
    customAxiosFetch<{ success: boolean }>({
      endpoint: `/client/${clientId}/set-primary-credit-card/${cardId}`,
      config: { method: 'POST' },
      callback: (result) => {
        if (!result?.success) return;
        getSavedCreditCards();
      },
      finalCallback: () => handleCloseModal()
    });
  }

  function handleResetCoolOff() {
    customAxiosFetch<WEFResponse<string>>({
      endpoint: `/client/${clientId}/reset-cool-off`,
      config: { method: 'POST' },
      callback: (result) => {
        if (!result?.success) return;
        getSavedCreditCards();
      },
      finalCallback: () => handleCloseModal()
    });
  }

  const handleCloseModal = () => setModalMetaData(null);

  const handleOpenDeleteModal = (cardId: string) => {
    setModalMetaData({
      title: 'Delete Credit Card',
      bodyText: 'Are you sure you want to permanently delete this card?',
      onConfirm: () => handleDeleteCard(cardId)
    });
  };

  const handleOpenSetPrimaryModal = (cardId: string) => {
    setModalMetaData({
      title: 'Set as Primary Card',
      bodyText: 'Set this card as the primary payment method?',
      onConfirm: () => handleSetPrimaryCard(cardId)
    });
  };

  const handleOpenResetCoolOffModal = () => {
    setModalMetaData({
      title: 'Reset Cool-Off Period',
      bodyText:
        'This will allow all cards to be charged again, bypassing the cool-off period. Are you sure?',
      onConfirm: () => handleResetCoolOff()
    });
  };

  const hasCardsInCoolOff = savedCreditCards.some((card) => card.isInCoolOffPeriod);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntilCoolOffEnds = (coolOffEndsAt: string | null) => {
    if (!coolOffEndsAt) return null;
    const endDate = new Date(coolOffEndsAt);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getCardStatus = (card: SavedCreditCards) => {
    if (card.isExpired) return 'expired';
    if (card.isInCoolOffPeriod) return 'cooloff';
    return 'active';
  };

  const getCardBorderColor = (status: string) => {
    switch (status) {
      case 'expired':
        return 'border-danger';
      case 'cooloff':
        return 'border-warning';
      default:
        return 'border-success';
    }
  };

  return (
    <>
      {modalMetaData && (
        <Modal show={true} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{modalMetaData.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">{modalMetaData.bodyText}</div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button onClick={modalMetaData.onConfirm} variant="danger">
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <div className="mt-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <FiCreditCard size={24} />
            <h4 className="mb-0">Saved Credit Cards</h4>
          </div>
          {hasCardsInCoolOff && (
            <Button
              variant="outline-warning"
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={handleOpenResetCoolOffModal}>
              <FiClock size={14} />
              Reset Cool-Off
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-4 text-muted">Loading cards...</div>
        ) : savedCreditCards.length === 0 ? (
          <Alert variant="info" className="d-flex align-items-center gap-2">
            <FiAlertCircle />
            No saved credit cards. Add a card using the form above.
          </Alert>
        ) : (
          <Row xs={1} md={2} lg={3} className="g-3">
            {savedCreditCards.map((card) => {
              const status = getCardStatus(card);
              const daysUntilCoolOffEnds = getDaysUntilCoolOffEnds(card.coolOffEndsAt);

              return (
                <Col key={card.id}>
                  <Card className={`h-100 border-2 ${getCardBorderColor(status)} shadow-sm`}>
                    <Card.Body className="d-flex flex-column">
                      {/* Card Header */}
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <CardTypeIcon cardType={card.cardType} size={32} />
                          <div>
                            <div
                              className="fw-bold"
                              style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
                              {card.maskedCardNumber}
                            </div>
                            <small className="text-muted">Expires {card.expiry}</small>
                          </div>
                        </div>
                        {card.isPrimary && (
                          <Badge bg="primary" className="d-flex align-items-center gap-1">
                            <FiStar size={12} /> Primary
                          </Badge>
                        )}
                      </div>

                      {/* Status Alerts */}
                      {card.isExpired && (
                        <Alert
                          variant="danger"
                          className="py-2 px-3 mb-2 d-flex align-items-center gap-2">
                          <FiAlertTriangle />
                          <small className="fw-semibold">Card Expired</small>
                        </Alert>
                      )}

                      {card.isInCoolOffPeriod && !card.isExpired && (
                        <Alert variant="warning" className="py-2 px-3 mb-2">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <FiClock />
                            <small className="fw-semibold">Cool-Off Period Active</small>
                          </div>
                          <small className="d-block text-muted">
                            Last failed: {formatDate(card.lastFailedChargeAt)}
                          </small>
                          <small className="d-block">
                            <strong>
                              {daysUntilCoolOffEnds} day{daysUntilCoolOffEnds !== 1 ? 's' : ''}{' '}
                              remaining
                            </strong>{' '}
                            (ends {formatDate(card.coolOffEndsAt)})
                          </small>
                        </Alert>
                      )}

                      {status === 'active' && (
                        <div className="text-success d-flex align-items-center gap-1 mb-2">
                          <Badge bg="success" className="px-2 py-1">
                            Ready to Charge
                          </Badge>
                        </div>
                      )}

                      {/* Card Info */}
                      <div className="mt-auto">
                        {card.addedAt && (
                          <small className="text-muted d-flex align-items-center gap-1 mb-2">
                            <FiCalendar size={12} />
                            Added {formatDate(card.addedAt)}
                          </small>
                        )}

                        {/* Actions */}
                        <div className="d-flex gap-2 pt-2 border-top">
                          {!card.isPrimary && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                              onClick={() => handleOpenSetPrimaryModal(card.id)}>
                              <FiStar size={14} />
                              Set Primary
                            </Button>
                          )}
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className={`${
                              card.isPrimary ? 'flex-grow-1' : ''
                            } d-flex align-items-center justify-content-center gap-1`}
                            onClick={() => handleOpenDeleteModal(card.id)}>
                            <FiTrash2 size={14} />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>
    </>
  );
}
